import { FloatingDelayGroup, Placement } from '@floating-ui/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SnapStyleOption } from '../../../viewer/tools/utils/snap-style';
import { Icon } from '../icon/icon';
import { Tooltip } from '../tooltip/tooltip';

type Props = {
  value: SnapStyleOption[];
  onChange: (value: SnapStyleOption[]) => void;
  /**
   * Where the tooltips open, beside the buttons.
   * @default 'left-start'
   */
  tooltipPlacement?: Placement;
};

/**
 * Buttons for turning the snap styles on and off: which points of the geometry near the cursor are
 * snapped to, several at the same time. At least one is on.
 */
export const SnapStyleButtons = ({ tooltipPlacement = 'left-start', ...props }: Props) => {
  const { t } = useTranslation();
  const posRef = React.useRef<HTMLDivElement>(null);

  const styles: { value: SnapStyleOption; icon: IconProp; title: string; tooltip: string }[] = [
    {
      value: 'free',
      icon: ['fal', 'arrow-pointer'],
      title: t('section.snapping.style.free.text', { ns: 'skyviewDesign' }),
      tooltip: t('section.snapping.style.free.tooltip', { ns: 'skyviewDesign' }),
    },
    {
      value: 'corner',
      icon: ['fal', 'vector-square'],
      title: t('section.snapping.style.corner.text', { ns: 'skyviewDesign' }),
      tooltip: t('section.snapping.style.corner.tooltip', { ns: 'skyviewDesign' }),
    },
    {
      value: 'midpoint',
      icon: ['fal', 'arrows-to-dot'],
      title: t('section.snapping.style.midpoint.text', { ns: 'skyviewDesign' }),
      tooltip: t('section.snapping.style.midpoint.tooltip', { ns: 'skyviewDesign' }),
    },
    {
      value: 'center',
      icon: ['fal', 'circle-dot'],
      title: t('section.snapping.style.center.text', { ns: 'skyviewDesign' }),
      tooltip: t('section.snapping.style.center.tooltip', { ns: 'skyviewDesign' }),
    },
    {
      value: 'intersection',
      icon: ['fal', 'xmark'],
      title: t('section.snapping.style.intersection.text', { ns: 'skyviewDesign' }),
      tooltip: t('section.snapping.style.intersection.tooltip', { ns: 'skyviewDesign' }),
    },
  ];

  const toggle = (value: SnapStyleOption) => {
    const active = props.value.includes(value);

    // The last one that is on stays on, as nothing would be snapped to.
    if (active && props.value.length === 1) {
      return;
    }

    props.onChange(
      styles.map((x) => x.value).filter((x) => (x === value ? !active : props.value.includes(x))),
    );
  };

  return (
    <FloatingDelayGroup delay={{ open: 500, close: 100 }}>
      <Group
        aria-label={t('section.snapping.style.title', { ns: 'skyviewDesign' })}
        ref={posRef}
        role="group"
      >
        {styles.map((style) => {
          const active = props.value.includes(style.value);

          return (
            <Tooltip
              content={style.tooltip}
              key={style.value}
              mainAxisOffset={22}
              placement={tooltipPlacement}
              posRef={posRef}
              title={style.title}
            >
              <Button
                $active={active}
                aria-label={style.title}
                aria-pressed={active}
                type="button"
                onClick={() => toggle(style.value)}
              >
                <Icon fixedWidth={true} icon={style.icon} />
              </Button>
            </Tooltip>
          );
        })}
      </Group>
    </FloatingDelayGroup>
  );
};

const Group = styled.div`
  display: flex;
  gap: 0.1em;
`;

const Button = styled.button<{ $active: boolean }>`
  flex: 1 1 0;
  padding: 0.4em;
  border: none;
  font-size: 1.1em;
  cursor: pointer;
  color: ${(props) => (props.$active ? 'white' : 'inherit')};
  background-color: ${(props) =>
    props.$active ? props.theme.color.gray.dark : props.theme.color.gray.light};

  &:hover {
    background-color: ${(props) =>
      props.$active ? props.theme.color.gray.dark : props.theme.color.gray.medium};
  }
`;
